import React, { useEffect, useMemo, useState } from "react";
import { Tabs, Tab, Button, Spinner } from "react-bootstrap";
import { Search, Lock, FileText, AlertTriangle, Scroll, Film, Pin, PinOff, Play, ShieldCheck } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import {
  addWhitelistIp,
  clearWhitelist,
  fetchLoginAudit,
  fetchProjectLogs,
  fetchSshSessions,
  fetchWhitelist,
  fetchWhitelistSettings,
  getAllEmployees,
  getProjects,
  removeWhitelistIp,
  revokeEmployeeSession,
  setSessionPinned,
  setWhitelistEnabled,
} from "../../redux/actionCreator";
import CastPlayer from "./CastPlayer";
import {
  all_employees,
  employee_list_loading,
  employees_page_details,
  project_data,
} from "../../redux/commonReducer";
import "../Employees/employeesList.scss";
import "./access.scss";

const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");

export default function Access() {
  const [tab, setTab] = useState("users");
  return (
    <div className="access-page">
      <div className="page-header">
        <div>
          <h2 className="page-title">Audit & Access</h2>
          <small className="page-sub">
            See who has access, where they're coming from, and revoke if needed.
          </small>
        </div>
      </div>

      <Tabs
        id="access-tabs"
        activeKey={tab}
        onSelect={(k) => setTab(k)}
        className="access-tabs"
      >
        <Tab eventKey="users" title="Users">
          <UsersTab />
        </Tab>
        <Tab eventKey="attempts" title="Login Attempts">
          <LoginAttemptsTab />
        </Tab>
        <Tab eventKey="sessions" title="SSH Sessions">
          <SshSessionsTab />
        </Tab>
        <Tab eventKey="logs" title="Project Logs">
          <ProjectLogsTab />
        </Tab>
        <Tab eventKey="whitelist" title="Whitelist">
          <WhitelistTab />
        </Tab>
      </Tabs>
    </div>
  );
}

/* ---------------- Users tab ---------------- */

function UsersTab() {
  const dispatch = useDispatch();
  const employees = useSelector(all_employees) || [];
  const loading = useSelector(employee_list_loading);
  const pageDetails = useSelector(employees_page_details);
  const [page, setPage] = useState(0);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    dispatch(getAllEmployees({ page, limit: 10, keyword }));
  }, [page, keyword]);

  const refresh = () =>
    dispatch(getAllEmployees({ page, limit: 10, keyword }));

  const revoke = (emp) => {
    if (
      !window.confirm(
        `Revoke ${emp.email}'s current session? They'll need to log in again.`
      )
    )
      return;
    dispatch(revokeEmployeeSession(emp._id, (ok) => ok && refresh()));
  };

  return (
    <div className="dash-panel emp-panel">
      <div className="panel-head with-search">
        <h3>
          User sessions
          <span className="count-pill">
            {pageDetails?.totalCount ?? employees.length}
          </span>
        </h3>
        <div className="search-box">
          <span className="search-icon"><Search size={16} /></span>
          <input
            className="form-control"
            placeholder="Search by name"
            value={keyword}
            onChange={(e) => {
              setPage(0);
              setKeyword(e.target.value);
            }}
          />
        </div>
      </div>

      <div className="emp-table-wrap">
        <table className="emp-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Status</th>
              <th>Role</th>
              <th>Last login</th>
              <th>Last IP</th>
              <th className="text-end">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="empty-row">
                  <Spinner size="sm" animation="border" /> Loading…
                </td>
              </tr>
            ) : employees.length === 0 ? (
              <tr>
                <td colSpan={7} className="empty-row">
                  <div className="empty-state">
                    <div className="empty-art"><Lock size={40} strokeWidth={1.2} /></div>
                    <h4>Nothing to show</h4>
                    <p>Add employees to start tracking access.</p>
                  </div>
                </td>
              </tr>
            ) : (
              employees.map((emp) => (
                <tr key={emp._id}>
                  <td>
                    <div className="emp-cell">
                      <div className="emp-avatar">
                        {(emp.firstname || emp.email || "?")
                          .slice(0, 1)
                          .toUpperCase()}
                      </div>
                      <div>
                        <div className="emp-name">
                          {[emp.firstname, emp.lastname]
                            .filter(Boolean)
                            .join(" ") || "—"}
                        </div>
                        <div className="emp-id">
                          {emp.employee_id || "—"}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>{emp.email}</td>
                  <td>
                    <span
                      className={`status-badge ${emp.status || "approved"}`}
                    >
                      {emp.status || "—"}
                    </span>
                  </td>
                  <td>
                    <span className={`role-chip role-${emp.role}`}>
                      {emp.role}
                    </span>
                  </td>
                  <td>{fmt(emp.lastLoginAt)}</td>
                  <td>
                    <code className="ip-cell">{emp.systemIp || "—"}</code>
                  </td>
                  <td className="text-end">
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => revoke(emp)}
                      disabled={!emp.lastLoginAt}
                      title={
                        emp.lastLoginAt
                          ? "Revoke current session"
                          : "Never logged in"
                      }
                    >
                      Revoke
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pageDetails?.totalPages > 1 && (
        <div className="panel-footer">
          <span className="muted">{pageDetails?.page_status}</span>
          <div className="pager">
            <Button
              size="sm"
              variant="light"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              ← Prev
            </Button>
            <Button
              size="sm"
              variant="light"
              className="ms-2"
              disabled={page + 1 >= (pageDetails?.totalPages || 1)}
              onClick={() => setPage((p) => p + 1)}
            >
              Next →
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Project logs tab ---------------- */

function ProjectLogsTab() {
  const dispatch = useDispatch();
  const projects = useSelector(project_data) || [];
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    dispatch(getProjects({ page: 0, limit: 100 }));
  }, []);

  const options = useMemo(
    () =>
      projects.map((p) => ({
        value: p._id,
        label: p.project_name || p._id,
      })),
    [projects]
  );

  const load = async (projectId) => {
    setLoading(true);
    setError(null);
    setResult(null);
    const data = await dispatch(fetchProjectLogs(projectId));
    setLoading(false);
    if (data?.success) {
      setResult(data.data);
      if (data.message) setError(data.message);
    } else {
      setError(data?.message || "Failed to load logs");
    }
  };

  return (
    <div className="dash-panel emp-panel">
      <div className="panel-head with-search">
        <h3>Project access logs</h3>
        <div style={{ minWidth: 280 }}>
          <Select
            options={options}
            value={selected}
            placeholder="Pick a project…"
            onChange={(opt) => {
              setSelected(opt);
              if (opt) load(opt.value);
            }}
            isClearable
          />
        </div>
      </div>

      <div className="logs-body">
        {!selected ? (
          <div className="empty-state">
            <div className="empty-art"><FileText size={40} strokeWidth={1.2} /></div>
            <h4>Select a project</h4>
            <p>Choose one above to view its access logs.</p>
          </div>
        ) : loading ? (
          <div className="empty-state">
            <Spinner animation="border" />
          </div>
        ) : error && !result ? (
          <div className="empty-state">
            <div className="empty-art"><AlertTriangle size={40} strokeWidth={1.2} /></div>
            <h4>Unavailable</h4>
            <p>{error}</p>
          </div>
        ) : (
          <pre className="raw-logs">
            {JSON.stringify(result, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

/* ---------------- Login attempts tab ---------------- */

const OUTCOME_BADGE = {
  success: "success",
  bad_credentials: "warning",
  unknown_email: "secondary",
  blocked: "danger",
  pending: "secondary",
  locked: "danger",
  error: "danger",
};

function LoginAttemptsTab() {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [outcome, setOutcome] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    const res = await dispatch(
      fetchLoginAudit({ page, limit: 25, keyword, outcome })
    );
    setLoading(false);
    if (res?.status) {
      setRows(res.data || []);
      setTotalPages(res.totalPages || 1);
    } else {
      setRows([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, keyword, outcome]);

  return (
    <div className="dash-panel emp-panel">
      <div className="panel-head with-search">
        <h3>
          Login attempts <span className="count-pill">{rows.length}</span>
        </h3>
        <div className="d-flex gap-2 align-items-center">
          <select
            className="form-select form-select-sm"
            value={outcome}
            onChange={(e) => {
              setPage(0);
              setOutcome(e.target.value);
            }}
            style={{ maxWidth: 200 }}
          >
            <option value="">All outcomes</option>
            <option value="success">Success</option>
            <option value="bad_credentials">Bad credentials</option>
            <option value="unknown_email">Unknown email</option>
            <option value="locked">Locked</option>
            <option value="blocked">Blocked</option>
            <option value="pending">Pending</option>
            <option value="error">Error</option>
          </select>
          <div className="search-box">
            <span className="search-icon"><Search size={16} /></span>
            <input
              className="form-control"
              placeholder="email / ip / UA"
              value={keyword}
              onChange={(e) => {
                setPage(0);
                setKeyword(e.target.value);
              }}
            />
          </div>
          <Button size="sm" variant="light" onClick={load}>
            Refresh
          </Button>
        </div>
      </div>

      <div className="emp-table-wrap">
        <table className="emp-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Outcome</th>
              <th>Email / User</th>
              <th>Path</th>
              <th>IP</th>
              <th>User-Agent</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="empty-row">
                  <Spinner size="sm" animation="border" /> Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="empty-row">
                  <div className="empty-state">
                    <div className="empty-art"><Scroll size={40} strokeWidth={1.2} /></div>
                    <h4>No login attempts yet</h4>
                    <p>Every login (success or fail) shows up here.</p>
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r._id}>
                  <td>{fmt(r.when)}</td>
                  <td>
                    <span
                      className={`badge bg-${
                        OUTCOME_BADGE[r.outcome] || "secondary"
                      }`}
                    >
                      {r.outcome}
                    </span>
                  </td>
                  <td>
                    <div className="emp-name">{r.user?.name || "—"}</div>
                    <div className="emp-id">{r.email}</div>
                  </td>
                  <td>
                    <code className="small">{r.path}</code>
                  </td>
                  <td>
                    <code>{r.ip || "—"}</code>
                  </td>
                  <td className="small text-muted" style={{ maxWidth: 260 }}>
                    <div
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={r.user_agent}
                    >
                      {r.user_agent || "—"}
                    </div>
                  </td>
                  <td className="small">{r.reason || "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="panel-footer">
          <span className="muted">
            Page {page + 1} of {totalPages}
          </span>
          <div className="pager">
            <Button
              size="sm"
              variant="light"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              ← Prev
            </Button>
            <Button
              size="sm"
              variant="light"
              className="ms-2"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next →
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- SSH sessions tab ---------------- */

function SshSessionsTab() {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(null);

  const togglePin = (row) => {
    if (row.pinned) {
      if (!window.confirm("Unpin this session? It will auto-prune at its next retention window.")) return;
      dispatch(setSessionPinned(row._id, false, "", (ok) => ok && load()));
      return;
    }
    const reason = window.prompt(
      "Pin this session — it will be kept until you manually unpin.\n\nOptional reason (audit / incident #):",
      ""
    );
    if (reason === null) return; // cancelled
    dispatch(setSessionPinned(row._id, true, reason || "", (ok) => ok && load()));
  };

  const load = async () => {
    setLoading(true);
    const res = await dispatch(
      fetchSshSessions({ page, limit: 20, keyword })
    );
    setLoading(false);
    if (res?.status) {
      setRows(res.data || []);
      setTotalPages(res.totalPages || 1);
    } else {
      setRows([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, keyword]);

  const dur = (ms) => {
    if (!ms || ms < 0) return "—";
    const s = Math.round(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}m ${r}s`;
  };

  return (
    <div className="dash-panel emp-panel">
      <div className="panel-head with-search">
        <h3>
          SSH sessions <span className="count-pill">{rows.length}</span>
        </h3>
        <div className="d-flex gap-2 align-items-center">
          <div className="search-box">
            <span className="search-icon"><Search size={16} /></span>
            <input
              className="form-control"
              placeholder="Search server / host / user"
              value={keyword}
              onChange={(e) => {
                setPage(0);
                setKeyword(e.target.value);
              }}
            />
          </div>
          <Button size="sm" variant="light" onClick={load}>
            Refresh
          </Button>
        </div>
      </div>

      <div className="emp-table-wrap">
        <table className="emp-table">
          <thead>
            <tr>
              <th style={{ width: 32 }}></th>
              <th>User</th>
              <th>Project</th>
              <th>Server</th>
              <th>Opened</th>
              <th>Duration</th>
              <th>I/O</th>
              <th>Exit</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="empty-row">
                  <Spinner size="sm" animation="border" /> Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="empty-row">
                  <div className="empty-state">
                    <div className="empty-art"><Film size={40} strokeWidth={1.2} /></div>
                    <h4>No sessions yet</h4>
                    <p>
                      When a project member opens a terminal, the session shows
                      up here with a full replay (asciicast).
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r._id}>
                  <td className="text-center">
                    <button
                      className="btn btn-link p-0 m-0"
                      style={{
                        fontSize: 18,
                        textDecoration: "none",
                        color: r.pinned ? "#f59e0b" : "#cbd5e1",
                      }}
                      onClick={() => togglePin(r)}
                      title={
                        r.pinned
                          ? `Pinned${r.pin_reason ? `: ${r.pin_reason}` : ""} — click to unpin`
                          : "Click to pin (exempt from auto-prune)"
                      }
                    >
                      {r.pinned ? <Pin size={14} /> : <PinOff size={14} />}
                    </button>
                  </td>
                  <td>
                    <div className="emp-name">{r.user?.name || "—"}</div>
                    <div className="emp-id">{r.user?.email}</div>
                  </td>
                  <td>{r.project_name}</td>
                  <td>
                    <div>{r.server_label}</div>
                    <div className="emp-id">
                      <code>
                        {r.server_user}@{r.server_host}
                      </code>
                    </div>
                  </td>
                  <td>{fmt(r.opened_at)}</td>
                  <td>{dur(r.duration_ms)}</td>
                  <td>
                    <span className="text-muted small">
                      ↑ {r.bytes_in || 0}b · ↓ {r.bytes_out || 0}b
                    </span>
                  </td>
                  <td>
                    <code className="small">{r.exit_reason || "—"}</code>
                    {r.pinned && r.pin_reason && (
                      <div className="emp-id" title="Pin reason">
                        <Pin size={12} className="me-1" /> {r.pin_reason}
                      </div>
                    )}
                  </td>
                  <td className="text-end">
                    <Button
                      size="sm"
                      variant={r.has_recording ? "primary" : "light"}
                      disabled={!r.has_recording}
                      onClick={() => setPlaying(r)}
                      title={
                        r.has_recording
                          ? "Replay this session"
                          : "Recording not available"
                      }
                    >
                      <Play size={14} className="me-1" /> Replay
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="panel-footer">
          <span className="muted">
            Page {page + 1} of {totalPages}
          </span>
          <div className="pager">
            <Button
              size="sm"
              variant="light"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              ← Prev
            </Button>
            <Button
              size="sm"
              variant="light"
              className="ms-2"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next →
            </Button>
          </div>
        </div>
      )}

      <CastPlayer session={playing} onClose={() => setPlaying(null)} />
    </div>
  );
}

/* ---------------- Whitelist tab ---------------- */

function WhitelistTab() {
  const dispatch = useDispatch();
  const employees = useSelector(all_employees) || [];
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [path, setPath] = useState("");
  const [settings, setSettings] = useState(null);
  const [toggling, setToggling] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addIp, setAddIp] = useState("");
  const [addUserId, setAddUserId] = useState("");

  const loadSettings = async () => {
    const res = await dispatch(fetchWhitelistSettings());
    if (res?.status) setSettings(res.data);
  };

  const load = async () => {
    setLoading(true);
    const res = await dispatch(fetchWhitelist());
    setLoading(false);
    if (res?.status) {
      setRows(res.data || []);
      setPath(res.path || "");
    }
  };

  useEffect(() => {
    load();
    loadSettings();
    // Pre-load employees for the user dropdown — small list, single fetch.
    dispatch(getAllEmployees({ page: 0, limit: 200, keyword: "" }));
  }, []);

  // Picking a user auto-fills their last-seen IP (handy after a fresh login).
  // Editing the IP doesn't clear the user selection — admin may want both.
  const pickUser = (uid) => {
    setAddUserId(uid);
    if (uid && !addIp) {
      const u = employees.find((e) => String(e._id) === String(uid));
      if (u?.systemIp) setAddIp(u.systemIp);
    }
  };

  const submitAdd = (e) => {
    e?.preventDefault?.();
    const ip = addIp.trim();
    if (!ip) return;
    setAdding(true);
    dispatch(
      addWhitelistIp(ip, addUserId || null, (ok) => {
        setAdding(false);
        if (ok) {
          setAddIp("");
          setAddUserId("");
          load();
        }
      })
    );
  };

  const remove = (ip) => {
    if (!window.confirm(`Remove ${ip} from the whitelist?`)) return;
    dispatch(removeWhitelistIp(ip, (ok) => ok && load()));
  };
  const clearAll = () => {
    if (
      !window.confirm(
        "Clear the entire whitelist? Everyone will be kicked from the gateway."
      )
    )
      return;
    dispatch(clearWhitelist((ok) => ok && load()));
  };

  const toggleBlocking = async () => {
    const enabled = settings?.whitelist_enabled !== false;
    if (enabled) {
      const reason = window.prompt(
        "Disable IP blocking? The nginx allowlist will be set to `allow all;` " +
          "and every IP will be able to reach sub-apps (still subject to the " +
          "session-cookie auth gate).\n\nOptional reason (audit trail):",
        ""
      );
      if (reason === null) return;
      setToggling(true);
      const r = await dispatch(setWhitelistEnabled(false, reason || ""));
      setToggling(false);
      if (r?.status) { loadSettings(); load(); }
    } else {
      if (
        !window.confirm(
          "Re-enable IP blocking? The allowlist will be rebuilt from currently-active sessions — " +
            "logged-in users stay in, unknown IPs are blocked again."
        )
      )
        return;
      setToggling(true);
      const r = await dispatch(setWhitelistEnabled(true, ""));
      setToggling(false);
      if (r?.status) { loadSettings(); load(); }
    }
  };

  const blockingOn = settings?.whitelist_enabled !== false;

  return (
    <div className="dash-panel emp-panel">
      <div
        className={`wl-kill-switch ${blockingOn ? "on" : "off"}`}
        style={{
          margin: "12px 0",
          padding: "12px 14px",
          borderRadius: 8,
          border: `1px solid ${blockingOn ? "#86efac" : "#fca5a5"}`,
          background: blockingOn ? "#f0fdf4" : "#fef2f2",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <strong>
            <span className="d-inline-flex align-items-center gap-2">
              {blockingOn
                ? <><ShieldCheck size={14} /> IP blocking is ON</>
                : <><AlertTriangle size={14} /> IP blocking is OFF — all IPs allowed</>}
            </span>
          </strong>
          <div className="small text-muted">
            {blockingOn
              ? "Only IPs in the allowlist below can reach protected sub-apps."
              : "nginx is in open mode (`allow all;`). The session-cookie auth gate still applies."}
            {settings?.changed_at && (
              <>
                {" · Last changed "}
                {fmt(settings.changed_at)}
                {settings.changed_reason ? ` (${settings.changed_reason})` : ""}
              </>
            )}
          </div>
        </div>
        <Button
          size="sm"
          variant={blockingOn ? "outline-danger" : "success"}
          onClick={toggleBlocking}
          disabled={toggling}
        >
          {toggling ? (
            <Spinner size="sm" animation="border" />
          ) : blockingOn ? (
            "Disable blocking"
          ) : (
            "Re-enable blocking"
          )}
        </Button>
      </div>
      <div className="panel-head with-search">
        <h3>
          Live NGINX allowlist
          <span className="count-pill">{rows.length}</span>
        </h3>
        <div className="d-flex gap-2 align-items-center">
          {path && <code className="path-chip">{path}</code>}
          <Button size="sm" variant="light" onClick={load}>
            Refresh
          </Button>
          <Button
            size="sm"
            variant="outline-danger"
            onClick={clearAll}
            disabled={rows.length === 0 || !blockingOn}
            title={!blockingOn ? "Blocking is OFF — nothing to clear" : ""}
          >
            Clear all
          </Button>
        </div>
      </div>

      <form
        onSubmit={submitAdd}
        className="d-flex gap-2 align-items-center mb-3 flex-wrap"
        style={{
          padding: "10px 12px",
          background: "#f8fafc",
          border: "1px solid #e5e7eb",
          borderRadius: 8,
        }}
      >
        <strong className="small me-1">+ Allow:</strong>
        <select
          className="form-select form-select-sm"
          value={addUserId}
          onChange={(e) => pickUser(e.target.value)}
          style={{ maxWidth: 240 }}
          disabled={adding || !blockingOn}
          title="Optional — attribute the row to a user"
        >
          <option value="">— Pick a user (optional) —</option>
          {employees.map((u) => (
            <option key={u._id} value={u._id}>
              {[u.firstname, u.lastname].filter(Boolean).join(" ") || u.email}
              {u.systemIp ? ` · ${u.systemIp}` : ""}
            </option>
          ))}
        </select>
        <input
          className="form-control form-control-sm"
          placeholder="IPv4 or IPv6 — e.g. 203.0.113.42"
          value={addIp}
          onChange={(e) => setAddIp(e.target.value)}
          style={{ maxWidth: 220, fontFamily: "ui-monospace, monospace" }}
          disabled={adding || !blockingOn}
        />
        <Button
          type="submit"
          size="sm"
          variant="success"
          disabled={!addIp.trim() || adding || !blockingOn}
          title={!blockingOn ? "Blocking is OFF — nothing to add" : ""}
        >
          {adding ? <Spinner size="sm" animation="border" /> : "Add to allowlist"}
        </Button>
        {!blockingOn && (
          <small className="text-muted">
            Re-enable blocking above before adding entries.
          </small>
        )}
      </form>

      <div className="emp-table-wrap">
        <table className="emp-table">
          <thead>
            <tr>
              <th>IP</th>
              <th>Granted to</th>
              <th>Status</th>
              <th>Last login</th>
              <th className="text-end">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="empty-row">
                  <Spinner size="sm" animation="border" /> Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="empty-row">
                  <div className="empty-state">
                    <div className="empty-art"><ShieldCheck size={40} strokeWidth={1.2} /></div>
                    <h4>No IPs whitelisted</h4>
                    <p>
                      Nobody is currently allowed through the gateway.
                      Whitelist entries are added on login and cleared at
                      midnight.
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              rows.map(({ ip, user }) => (
                <tr key={ip}>
                  <td>
                    <code className="ip-cell">{ip}</code>
                  </td>
                  <td>
                    {user ? (
                      <div>
                        <div className="emp-name">{user.name}</div>
                        <div className="emp-id">{user.email}</div>
                      </div>
                    ) : (
                      <span className="muted">Unknown</span>
                    )}
                  </td>
                  <td>
                    {user ? (
                      <span
                        className={`status-badge ${user.status || "approved"}`}
                      >
                        {user.status || "—"}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>{user ? fmt(user.lastLoginAt) : "—"}</td>
                  <td className="text-end">
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => remove(ip)}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
